import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SalovareLoader from '../../components/common/SalovareLoader';
import PrimaryActionButton from '../../components/common/PrimaryActionButton';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import StockSummaryCards from '../../components/stock/StockSummaryCards';
import StockOverviewPanel from '../../components/stock/StockOverviewPanel';
import StockPurchasesPanel from '../../components/stock/StockPurchasesPanel';
import StockMovementsPanel from '../../components/stock/StockMovementsPanel';
import StockAdjustmentDialog from '../../components/stock/StockAdjustmentDialog';
import RecordStockPurchaseDialog from '../../components/stock/RecordStockPurchaseDialog';
import StockPurchaseDetailsDialog from '../../components/stock/StockPurchaseDetailsDialog';
import SuppliersPanel from '../../components/stock/SuppliersPanel';
import SupplierEditorDialog from '../../components/stock/SupplierEditorDialog';
import { useToast } from '../../context/ToastContext';
import { useAppData } from '../../context/AppDataContext';
import usePermissions from '../../hooks/usePermissions';
import { Permissions } from '../../constants/Permissions';
import { STOCK_PAGE_SIZE } from '../../constants/Stock';
import StockService from '../../services/stockService';
import SupplierService from '../../services/supplierService';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { describeApiError } from '../../utils/stockErrorUtils';

const StockSection = ({ activeTab = 'stock' }) => {
  const { t } = useTranslation();
  const { storeId } = useParams();
  const { currentStore } = useAppData();
  const { showSuccess, showError } = useToast();
  const { hasPermission } = usePermissions(storeId);
  const canSeeCosts = hasPermission(Permissions.StockCostsRead);
  const canCreatePurchase = hasPermission(Permissions.StockPurchasesCreate);
  const canVoidPurchase = hasPermission(Permissions.StockPurchasesVoid);
  const canAdjust = hasPermission(Permissions.StockAdjust);
  const canManageSuppliers = hasPermission(Permissions.SuppliersManage);
  const canCreateProducts = hasPermission(Permissions.ProductsCreate);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [movementType, setMovementType] = useState('');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState({ items: [], totalCount: 0, lowStockCount: 0, negativeStockCount: 0, stockValueEstimate: null });
  const [movements, setMovements] = useState({ items: [], totalCount: 0 });
  const [purchases, setPurchases] = useState({ items: [], totalCount: 0 });
  const [suppliers, setSuppliers] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [adjustmentOpen, setAdjustmentOpen] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [purchaseToVoid, setPurchaseToVoid] = useState(null);
  const [supplierDialog, setSupplierDialog] = useState({ open: false, supplier: null });
  const [supplierToArchive, setSupplierToArchive] = useState(null);
  const [saving, setSaving] = useState(false);
  const [voidWarning, setVoidWarning] = useState('');
  const currency = currentStore?.currency || 'EUR';

  const load = useCallback(() => {
    if (!storeId) {
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    const run = async () => {
      try {
        if (activeTab === 'stock' || activeTab === 'adjustments') {
          const data = await requestDeduplicator.deduplicate(
            `stock-products-${storeId}-${search}-${page}-${activeTab}`,
            () => StockService.getProducts(storeId, { search, page, pageSize: STOCK_PAGE_SIZE }),
          );
          if (!cancelled) {
            setOverview(data);
            setProductOptions(data.items);
          }
        } else if (activeTab === 'movements') {
          const [data, productPage] = await requestDeduplicator.deduplicate(
            `stock-movements-${storeId}-${movementType}-${page}`,
            () => Promise.all([
              StockService.getMovements(storeId, { movementType, page, pageSize: STOCK_PAGE_SIZE }),
              StockService.getProducts(storeId, { page: 1, pageSize: 100 }),
            ]),
          );
          const contentByProductId = new Map(
            productPage.items.map((product) => [String(product.id), product.contentQuantity]),
          );
          if (!cancelled) {
            setMovements({
              ...data,
              items: data.items.map((item) => ({
                ...item,
                contentQuantity: contentByProductId.get(String(item.productId)) || item.contentQuantity,
              })),
            });
          }
        } else if (activeTab === 'purchases' && canSeeCosts) {
          const [purchasePage, supplierList, productPage] = await requestDeduplicator.deduplicate(
            `stock-purchases-${storeId}-${page}`,
            () => Promise.all([
              StockService.getPurchases(storeId, { page, pageSize: STOCK_PAGE_SIZE }),
              SupplierService.getSuppliers(storeId),
              StockService.getProducts(storeId, { page: 1, pageSize: 100 }),
            ]),
          );
          if (!cancelled) {
            setPurchases(purchasePage);
            setSuppliers(supplierList);
            setProductOptions(productPage.items);
          }
        } else if (activeTab === 'suppliers') {
          const supplierList = await requestDeduplicator.deduplicate(
            `suppliers-${storeId}`,
            () => SupplierService.getSuppliers(storeId),
          );
          if (!cancelled) setSuppliers(supplierList);
        }
      } catch (error) {
        console.error('Failed to load stock:', error);
        if (!cancelled) showError(describeApiError(error, t, 'stock.errors.load'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [activeTab, canSeeCosts, movementType, page, search, showError, storeId, t]);

  useEffect(() => load(), [load]);

  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  const handleSaveAdjustment = async (adjustment) => {
    try {
      setSaving(true);
      await StockService.createAdjustment(storeId, adjustment);
      showSuccess(t('stock.adjustments.saved'));
      setAdjustmentOpen(false);
      load();
    } catch (error) {
      console.error('Failed to adjust stock:', error);
      showError(describeApiError(error, t, 'stock.errors.adjust'));
    } finally {
      setSaving(false);
    }
  };

  const handleSavePurchase = async (purchase) => {
    try {
      setSaving(true);
      await StockService.createPurchase(storeId, purchase);
      showSuccess(t('stock.purchases.saved'));
      setPurchaseOpen(false);
      load();
    } catch (error) {
      console.error('Failed to record purchase:', error);
      showError(describeApiError(error, t, 'stock.errors.purchase'));
    } finally {
      setSaving(false);
    }
  };

  const handleVoidPurchase = async () => {
    if (!purchaseToVoid) return;
    try {
      setSaving(true);
      const result = await StockService.voidPurchase(storeId, purchaseToVoid.id, t('stock.purchases.voidReason'));
      if (result.productsNowNegative?.length) {
        setVoidWarning(t('stock.purchases.negativeWarning', {
          products: result.productsNowNegative.map((item) => item.name).join(', '),
        }));
      }
      showSuccess(t('stock.purchases.voided'));
      setPurchaseToVoid(null);
      load();
    } catch (error) {
      console.error('Failed to void purchase:', error);
      showError(describeApiError(error, t, 'stock.errors.voidPurchase'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSupplier = async (supplier) => {
    try {
      setSaving(true);
      if (supplierDialog.supplier?.id) {
        await SupplierService.updateSupplier(storeId, supplierDialog.supplier.id, supplier);
      } else {
        await SupplierService.createSupplier(storeId, supplier);
      }
      showSuccess(t('stock.suppliers.saved'));
      setSupplierDialog({ open: false, supplier: null });
      load();
    } catch (error) {
      console.error('Failed to save supplier:', error);
      showError(describeApiError(error, t, 'stock.errors.supplier'));
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveSupplier = async () => {
    if (!supplierToArchive) return;
    try {
      setSaving(true);
      await SupplierService.archiveSupplier(storeId, supplierToArchive.id);
      showSuccess(t('stock.suppliers.archived'));
      setSupplierToArchive(null);
      load();
    } catch (error) {
      console.error('Failed to archive supplier:', error);
      showError(describeApiError(error, t, 'stock.errors.supplier'));
    } finally {
      setSaving(false);
    }
  };

  if (loading && activeTab !== 'adjustments') {
    return <SalovareLoader size="medium" center sx={{ py: 6 }} />;
  }

  return (
    <>
        <Box role="tabpanel" hidden={activeTab !== 'stock'} id="stock-tabpanel-stock">
          {activeTab === 'stock' ? (
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <StockSummaryCards
                lowStockCount={overview.lowStockCount}
                negativeStockCount={overview.negativeStockCount}
                stockValueEstimate={overview.stockValueEstimate}
                canSeeCosts={canSeeCosts}
                currency={currency}
              />
              <StockOverviewPanel
                items={overview.items}
                totalCount={overview.totalCount}
                page={page}
                pageSize={STOCK_PAGE_SIZE}
                search={search}
                canSeeCosts={canSeeCosts}
                onSearchChange={(value) => {
                  setSearch(value);
                  setPage(1);
                }}
                onPageChange={setPage}
                currency={currency}
              />
            </Box>
          ) : null}
        </Box>
        {canSeeCosts ? (
          <Box role="tabpanel" hidden={activeTab !== 'purchases'} id="stock-tabpanel-purchases">
            {activeTab === 'purchases' ? (
              <StockPurchasesPanel
                items={purchases.items}
                totalCount={purchases.totalCount}
                page={page}
                pageSize={STOCK_PAGE_SIZE}
                currency={currency}
                canCreate={canCreatePurchase}
                canVoid={canVoidPurchase}
                onPageChange={setPage}
                onRecord={() => setPurchaseOpen(true)}
                onOpen={setSelectedPurchase}
                onVoid={setPurchaseToVoid}
              />
            ) : null}
          </Box>
        ) : null}
        <Box role="tabpanel" hidden={activeTab !== 'movements'} id="stock-tabpanel-movements">
          {activeTab === 'movements' ? (
            <StockMovementsPanel
              items={movements.items}
              totalCount={movements.totalCount}
              page={page}
              pageSize={STOCK_PAGE_SIZE}
              movementType={movementType}
              canSeeCosts={canSeeCosts}
              onMovementTypeChange={(value) => {
                setMovementType(value);
                setPage(1);
              }}
              onPageChange={setPage}
              currency={currency}
            />
          ) : null}
        </Box>
        {canAdjust ? (
          <Box role="tabpanel" hidden={activeTab !== 'adjustments'} id="stock-tabpanel-adjustments">
            {activeTab === 'adjustments' ? (
              <Box id="stock-adjustments-panel" sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="body1" sx={{ mb: 2 }}>{t('stock.adjustments.intro')}</Typography>
                <PrimaryActionButton id="open-stock-adjustment" onClick={() => setAdjustmentOpen(true)}>
                  {t('stock.adjustments.open')}
                </PrimaryActionButton>
              </Box>
            ) : null}
          </Box>
        ) : null}
        <Box role="tabpanel" hidden={activeTab !== 'suppliers'} id="stock-tabpanel-suppliers">
          {activeTab === 'suppliers' ? (
            <SuppliersPanel
              suppliers={suppliers}
              canManage={canManageSuppliers}
              onCreate={() => setSupplierDialog({ open: true, supplier: null })}
              onEdit={(supplier) => setSupplierDialog({ open: true, supplier })}
              onArchive={setSupplierToArchive}
            />
          ) : null}
        </Box>
      {voidWarning ? <Alert severity="warning" sx={{ m: 2 }} onClose={() => setVoidWarning('')}>{voidWarning}</Alert> : null}

      <StockAdjustmentDialog
        open={adjustmentOpen}
        products={productOptions}
        currency={currency}
        saving={saving}
        onClose={() => setAdjustmentOpen(false)}
        onSave={handleSaveAdjustment}
      />
      <RecordStockPurchaseDialog
        open={purchaseOpen}
        products={productOptions}
        suppliers={suppliers}
        currency={currency}
        canCreateProducts={canCreateProducts}
        saving={saving}
        onClose={() => setPurchaseOpen(false)}
        onSave={handleSavePurchase}
      />
      <StockPurchaseDetailsDialog
        open={Boolean(selectedPurchase)}
        purchase={selectedPurchase}
        currency={currency}
        onClose={() => setSelectedPurchase(null)}
      />
      <SupplierEditorDialog
        open={supplierDialog.open}
        supplier={supplierDialog.supplier}
        saving={saving}
        onClose={() => setSupplierDialog({ open: false, supplier: null })}
        onSave={handleSaveSupplier}
      />
      <ConfirmDialog
        open={Boolean(purchaseToVoid)}
        onClose={() => setPurchaseToVoid(null)}
        onConfirm={handleVoidPurchase}
        title={t('stock.purchases.voidTitle')}
        message={t('stock.purchases.voidMessage')}
        confirmText={t('stock.purchases.void')}
        cancelText={t('common.cancel')}
      />
      <ConfirmDialog
        open={Boolean(supplierToArchive)}
        onClose={() => setSupplierToArchive(null)}
        onConfirm={handleArchiveSupplier}
        title={t('stock.suppliers.archiveTitle')}
        message={t('stock.suppliers.archiveMessage', { name: supplierToArchive?.name || '' })}
        confirmText={t('stock.suppliers.archive')}
        cancelText={t('common.cancel')}
      />
    </>
  );
};

export const StockToProductsRedirect = () => {
  const { storeId } = useParams();
  const [params] = useSearchParams();
  const tab = params.get('tab');
  const nextTab = !tab || tab === 'overview' ? 'stock' : tab;
  const target = storeId
    ? `/management/stores/${storeId}/products?tab=${encodeURIComponent(nextTab)}`
    : '/management/products';

  return <Navigate to={target} replace />;
};

export default StockSection;
